import { Badge } from '@bambi/component-library/src/components/Badge/Badge';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { truncate } from 'lodash-es';
import { DateTime } from 'luxon';

import store from 'src/app/store/store';
import { DriverColumnFilter } from 'src/common/datagridColumnFilters/DriverColumnFilter';
import {
  InvoiceTripFinderRead,
  TripListRead,
  TripRead,
} from 'src/common/external/bambi-api/bambiApi';
import { formatAddress } from 'src/common/formatAddress';
import { formatPriceCell } from 'src/common/formatPriceCell';
import { HereMapsAddressLink } from 'src/common/HereMapsAddressLink';
import { PassengerPopover } from 'src/features/passenger/management/PassengerPopover';
import { PriceSummaryModalButton } from 'src/features/pricing/PriceSummaryModal/PriceSummaryModalButton';
import { TripGridRowActions } from 'src/features/trip/management/TripManagementDataGrid/TripGridRowActions';

import {
  setFilterCanceledReasons,
  setFilterIncludeNote,
  setFilterSpaceTypes,
  setFilterVehicles,
  setSelectedDateRange,
  setSelectedDrivers,
  setSelectedPayers,
  setSelectedStatuses,
  tripSlice,
} from '../../trip.slice';
import { CreatedByCell } from '../CreatedByCell';
import CanceledReasonsColumnFilter from '../filters/CanceledReasonsColumnFilter';
import { DateColumnFilter } from '../filters/DateColumnFilter';
import { PayersColumnFilter } from '../filters/PayerColumnFilter';
import { PriceColumnFilter } from '../filters/PriceColumnFilter';
import SpaceTypeColumnFilter from '../filters/SpaceTypeColumnFilter';
import { StatusColumnFilter } from '../filters/StatusColumnFilter';
import { TagColumnFilter } from '../filters/TagColumnFilter';
import { VehicleColumnFilter } from '../filters/VehicleColumnFilter';
import { TripDriver } from '../TripDriver';
import { TripImportedBadge } from '../TripImportedBadge';
import TripTagsDisplay from '../TripTagsDisplay';
import { PayerDisplayCell } from './cells/PayerDisplayCell';
import { TripStatusCellContent } from './TripStatusCellContent';

const columnHelper = createColumnHelper<TripListRead | TripRead>();

const rowSelectionColumnDef = columnHelper.display({
  id: 'selection',
  header: () => <div data-testid="row-selection-column" />,
  enableHiding: false,
  meta: {
    enableHeaderMenu: false,
  },
  cell: ({ row }) => {
    if (
      row.original.status === 'canceled' ||
      row.original.payer.has_kinetik_account
    ) {
      return null;
    }

    return (
      <input
        data-testid={`row-selection-control-${row.original.id}`}
        type="checkbox"
        checked={row.getIsSelected()}
        onChange={row.getToggleSelectedHandler()}
      />
    );
  },
});

const actionColumnDef = (slim?: boolean) =>
  columnHelper.display({
    id: 'actions',
    header: '',
    enableHiding: false,
    cell: ({ row }) => {
      return (
        <TripGridRowActions
          trip={row.original}
          triggerClassName={slim ? 'px-0 py-1' : undefined}
        />
      );
    },
    meta: {
      columnClassName: slim ? 'px-0 py-0' : undefined,
    },
  });

export const passengerColumnDef = columnHelper.accessor('passenger', {
  enableSorting: true,
  id: 'name',
  header: 'Passenger',
  cell: (props) => (
    <PassengerPopover passenger={props.row.original.passenger} />
  ),
});

export const scheduledPickupAtColumnDef = columnHelper.accessor(
  'scheduled_pickup_at',
  {
    enableSorting: true,
    id: 'scheduled_pickup_at',
    header: 'Date of Trip',
    cell: (props) =>
      DateTime.fromISO(props.row.original.scheduled_pickup_at).toLocaleString({
        weekday: 'short',
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
      }),
    meta: {
      filterComponent(column, open, onClose) {
        return (
          <DateColumnFilter
            open={open}
            onClose={onClose}
            setSelectedAction={setSelectedDateRange}
          />
        );
      },
      filterCount() {
        const state = store.getState().trip;
        return state.selectedDateRange?.from || state.selectedDateRange?.to
          ? 1
          : 0;
      },
    },
  }
);

export const pickupTimeColumnDef = columnHelper.display({
  header: 'Pickup Time',
  cell: (props) =>
    DateTime.fromISO(props.row.original.scheduled_pickup_at).toFormat('t'),
});

export const pickupAddressColumnDef = columnHelper.accessor('pickup', {
  enableSorting: true,
  id: 'pickup_address',
  header: 'Pick Up Address',
  cell: ({ row }) => (
    <HereMapsAddressLink
      addresses={[
        {
          latitude: row.original.pickup.latitude,
          longitude: row.original.pickup.longitude,
        },
        {
          latitude: row.original.dropoff.latitude,
          longitude: row.original.dropoff.longitude,
        },
      ]}
    >
      {formatAddress(row.original.pickup.address)}
    </HereMapsAddressLink>
  ),
});

export const dropoffAddressColumnDef = columnHelper.accessor('dropoff', {
  enableSorting: true,
  id: 'dropoff_address',
  header: 'Drop Off Address',
  cell: ({ row }) => (
    <HereMapsAddressLink
      addresses={[
        {
          latitude: row.original.pickup.latitude,
          longitude: row.original.pickup.longitude,
        },
        {
          latitude: row.original.dropoff.latitude,
          longitude: row.original.dropoff.longitude,
        },
      ]}
    >
      {formatAddress(row.original.dropoff.address)}
    </HereMapsAddressLink>
  ),
});

const createdByColumnDef = columnHelper.accessor('created_by', {
  id: 'created_by',
  header: 'Created By',
  cell: (props) => <CreatedByCell id={props.row.original.created_by} />,
});

const estimatedDistanceMilesColumnDef = columnHelper.accessor(
  'estimated_distance_miles',
  {
    id: 'estimated_distance_miles',
    header: 'Estimated Miles',
    cell: (props) => (
      <>{props.row.original.estimated_distance_miles?.toFixed(2)}</>
    ),
  }
);

const inputPriceCentsColumnDef = columnHelper.accessor('input_price_cents', {
  id: 'input_price_cents',
  enableSorting: true,
  header: 'Price',
  cell: (props) => {
    const { input_price_cents } = props.row.original;
    const formattedPrice = formatPriceCell(input_price_cents);
    return (
      <div className="flex items-center gap-1">
        {formattedPrice}
        <PriceSummaryModalButton trip={props.row.original} />
      </div>
    );
  },
  meta: {
    filterComponent(column, open, onClose) {
      return <PriceColumnFilter open={open} onClose={onClose} />;
    },
    filterCount() {
      const state = store.getState().trip;
      return state.filterPrice !== null ? 1 : 0;
    },
  },
});

const spaceTypeColumnDef = columnHelper.accessor('space_type', {
  id: 'space_type',
  header: 'Space Type',
  cell: (props) => <Badge label={props.row.original.space_type} color="blue" />,
  meta: {
    filterComponent(column, open, onClose) {
      return (
        <SpaceTypeColumnFilter
          open={open}
          onClose={onClose}
          selected={store.getState().trip.filterSpaceTypes}
          onApply={(spaceTypes) => {
            store.dispatch(setFilterSpaceTypes(spaceTypes));
          }}
          triggerElement={<div />}
        />
      );
    },
    filterCount() {
      const state = store.getState().trip;
      return state.filterSpaceTypes.length || 0;
    },
  },
});

export const avoidMultiloadColumnDef = columnHelper.accessor(
  'should_avoid_multiload' as any,
  {
    id: 'should_avoid_multiload',
    header: 'Do not multiload',
    cell: ({ getValue }) =>
      getValue() ? <Badge label="Yes" color="red" /> : null,
  }
);

export const payerColumnDef = columnHelper.accessor('payer', {
  enableSorting: true,
  id: 'payer_name',
  header: 'Payer',
  cell: (props) => {
    return <PayerDisplayCell payer={props.row.original.payer} />;
  },
  meta: {
    filterComponent(column, open, onClose) {
      return (
        <PayersColumnFilter
          open={open}
          onClose={onClose}
          selected={store.getState().trip.selectedPayers}
          setSelectedAction={setSelectedPayers}
        />
      );
    },
    filterCount() {
      const state = store.getState().trip;
      return state.selectedPayers.length || 0;
    },
  },
});

// TODO: Move trip finder stuff out of this file
const tripFinderPayerColumnDef = columnHelper.accessor('payer', {
  id: 'payer_name',
  header: 'Payer',
  cell: (props) => {
    const trip = props.row.original as unknown as InvoiceTripFinderRead;
    return (
      <>
        {trip.payer.payer_display_name
          ? truncate(trip.payer.payer_display_name, {
              length: 30,
              omission: '...',
            })
          : ''}
      </>
    );
  },
  meta: {
    filterComponent(column, open, onClose) {
      return (
        <PayersColumnFilter
          open={open}
          onClose={onClose}
          selected={store.getState().trip.selectedPayers}
          setSelectedAction={setSelectedPayers}
        />
      );
    },
    filterCount() {
      const state = store.getState().trip;
      return state.selectedPayers.length || 0;
    },
  },
});

const driverColumnDef = columnHelper.accessor('driver_name', {
  id: 'driver_name',
  header: 'Driver',
  cell: (props) => <TripDriver {...props.row.original} />,
  enableColumnFilter: true,
  meta: {
    filterComponent(column, open, onClose) {
      return (
        <DriverColumnFilter
          open={open}
          onClose={onClose}
          selected={store.getState().trip.selectedDrivers}
          setSelectedAction={setSelectedDrivers}
          includeNoteFilter
          setIncludeNoteAction={setFilterIncludeNote}
        />
      );
    },
    filterCount() {
      return store.getState().trip.selectedDrivers.length;
    },
  },
});

const statusColumnDef = columnHelper.accessor('status', {
  enableSorting: true,
  id: 'status',
  header: 'Status',
  cell: (props) => <TripStatusCellContent trip={props.row.original} />,
  meta: {
    filterComponent(column, open, onClose) {
      return (
        <StatusColumnFilter
          open={open}
          onClose={onClose}
          selected={store.getState().trip.selectedStatuses}
          setSelectedAction={setSelectedStatuses}
        />
      );
    },
    filterCount() {
      const state = store.getState().trip;
      return state.selectedStatuses.length;
    },
  },
});

const confirmationNumberColumnDef = columnHelper.accessor('confirmation_code', {
  id: 'confirmation_code',
  header: 'Confirmation #',
  cell: (props) => {
    const { confirmation_code } = props.row.original;
    if (confirmation_code) {
      return confirmation_code;
    }
    return '';
  },
});

const tagsColumnDef = columnHelper.accessor('tags', {
  header: 'Tags',
  cell: (props) => <TripTagsDisplay tags={props.row.original.tags} />,
  meta: {
    filterComponent(column, open, onClose) {
      return <TagColumnFilter open={open} onClose={onClose} />;
    },
    filterCount() {
      const state = store.getState().trip;
      return state.selectedTags.length || 0;
    },
  },
});

const invoiceNumberColumnDef = columnHelper.accessor('invoice_numbers', {
  id: 'invoice_numbers',
  header: 'Invoice #',
  cell: ({ row }) => row.original.invoice_numbers,
});

const importedTripColumn = columnHelper.display({
  id: 'imported',
  header: 'Imported',
  cell: ({ row }) => <TripImportedBadge trip={row.original} />,
});

const vehicleNameColumn = columnHelper.accessor('vehicle_nickname', {
  id: 'vehicle_nickname',
  header: 'Vehicle',
  cell: ({ getValue }) => getValue(),
  meta: {
    filterComponent(column, open, onClose) {
      return (
        <VehicleColumnFilter
          open={open}
          onClose={onClose}
          selected={store.getState().trip.filterVehicles}
          setSelectedAction={setFilterVehicles}
        />
      );
    },
    filterCount() {
      return store.getState().trip.filterVehicles.length;
    },
  },
});

const externalTripIdColumnDef = columnHelper.accessor('external_trip_id', {
  id: 'external_trip_id',
  header: 'External Trip ID',
  cell: (props) => {
    const { external_trip_id } = props.row.original;
    if (external_trip_id) {
      return external_trip_id;
    }
    return '';
  },
});

export const createdAtColumnDef = columnHelper.accessor('created_at', {
  enableSorting: true,
  id: 'created_at',
  header: 'Date Created',
  cell: (props) =>
    DateTime.fromISO(props.row.original.created_at).toLocaleString({
      weekday: 'short',
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    }),
  meta: {
    filterComponent(column, open, onClose) {
      return (
        <DateColumnFilter
          open={open}
          onClose={onClose}
          setSelectedAction={tripSlice.actions.setSelectedCreatedAtDateRange}
        />
      );
    },
    filterCount() {
      const state = store.getState().trip;
      return state.selectedCreatedAtDateRange?.from ||
        state.selectedCreatedAtDateRange?.to
        ? 1
        : 0;
    },
  },
});

export const canceledReasonColumnDef = columnHelper.accessor(
  'canceled_reason',
  {
    id: 'canceled_reason',
    header: 'Cancellation Reason',
    meta: {
      filterComponent(column, open, onClose) {
        return (
          <CanceledReasonsColumnFilter
            open={open}
            onClose={onClose}
            selected={store.getState().trip.filterCanceledReasons}
            onApply={(canceledReasons) => {
              store.dispatch(setFilterCanceledReasons(canceledReasons));
            }}
            triggerElement={<div />}
          />
        );
      },
      filterCount() {
        const state = store.getState().trip;
        return state.filterCanceledReasons.length || 0;
      },
    },
  }
);

export const columns: ColumnDef<TripListRead | TripRead, any>[] = [
  rowSelectionColumnDef,
  actionColumnDef(true),
  passengerColumnDef,
  scheduledPickupAtColumnDef,
  pickupTimeColumnDef,
  pickupAddressColumnDef,
  dropoffAddressColumnDef,
  estimatedDistanceMilesColumnDef,
  inputPriceCentsColumnDef,
  spaceTypeColumnDef,
  payerColumnDef,
  statusColumnDef,
  tagsColumnDef,
  driverColumnDef,
  confirmationNumberColumnDef,
  invoiceNumberColumnDef,
  importedTripColumn,
  vehicleNameColumn,
  externalTripIdColumnDef,
  createdAtColumnDef,
  avoidMultiloadColumnDef,
  canceledReasonColumnDef,
];

export const rideOrderingPortalColumns: ColumnDef<
  TripListRead | TripRead,
  any
>[] = [
  actionColumnDef(false),
  passengerColumnDef,
  scheduledPickupAtColumnDef,
  pickupTimeColumnDef,
  pickupAddressColumnDef,
  dropoffAddressColumnDef,
  estimatedDistanceMilesColumnDef,
  spaceTypeColumnDef,
  payerColumnDef,
  driverColumnDef,
  statusColumnDef,
  confirmationNumberColumnDef,
  createdByColumnDef,
  tagsColumnDef,
  createdAtColumnDef,
];

export const tripFinderModalColumns: ColumnDef<InvoiceTripFinderRead, any>[] = [
  passengerColumnDef,
  invoiceNumberColumnDef,
  scheduledPickupAtColumnDef,
  pickupTimeColumnDef,
  pickupAddressColumnDef,
  dropoffAddressColumnDef,
  estimatedDistanceMilesColumnDef,
  inputPriceCentsColumnDef,
  spaceTypeColumnDef,
  tripFinderPayerColumnDef,
  driverColumnDef,
  statusColumnDef,
  confirmationNumberColumnDef,
  tagsColumnDef,
  vehicleNameColumn,
  createdAtColumnDef,
] as unknown as ColumnDef<InvoiceTripFinderRead, any>[];
